import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ExternalLink, CheckCircle2, Circle, Store, Package, Layers, Plus, Trash2, Pencil, Ban, User } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const useShopSettings = () => useQuery({ queryKey: ["shop-settings"], queryFn: async () => (await api.get("/shop/admin/settings")).data });
const useShopDashboard = () => useQuery({ queryKey: ["shop-dashboard"], queryFn: async () => (await api.get("/shop/admin/dashboard")).data });
const useShopCategories = () => useQuery({ queryKey: ["shop-categories"], queryFn: async () => (await api.get("/shop/admin/categories")).data });

const STATUS_STYLE = { draft: "bg-slate-200 text-slate-700", maintenance: "bg-amber-100 text-amber-700", live: "bg-emerald-100 text-emerald-700" };

function StatusBadge({ status, testId = "shop-status-badge" }) {
  const { t } = useI18n();
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.draft}`} data-testid={testId}>{t(`shop.status.${status}`)}</span>;
}

/* ---------------- Dashboard ---------------- */
function DashboardTab({ settings }) {
  const { t } = useI18n();
  const { data, isLoading } = useShopDashboard();
  if (isLoading || !data) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const steps = [
    ["branding", t("shop.dashboard.step_branding")], ["slug", t("shop.dashboard.step_slug")],
    ["legal", t("shop.dashboard.step_legal")], ["products", t("shop.dashboard.step_products")],
    ["live", t("shop.dashboard.step_live")],
  ];
  const doneMap = Object.fromEntries((data.setup.checklist || []).map((c) => [c.key, c.done]));
  const counts = data.counts || {};
  return (
    <div className="space-y-6" data-testid="shop-dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[["published", counts.published], ["draft", counts.draft], ["hidden", counts.hidden], ["low_stock", data.low_stock]].map(([k, v]) => (
          <Card key={k} className="p-4" data-testid={`shop-stat-${k}`}>
            <div className="text-xs uppercase tracking-wide text-slate-500">{t(`shop.dashboard.${k}`)}</div>
            <div className="mt-1 text-3xl font-bold tabular-nums">{v ?? 0}</div>
          </Card>
        ))}
      </div>
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.dashboard.setup")}</h3>
          <span className="text-sm text-slate-500">{data.setup.done}/{data.setup.total}</span>
        </div>
        <ul className="space-y-2">
          {steps.map(([key, label]) => (
            <li key={key} className="flex items-center gap-2 text-sm" data-testid={`shop-step-${key}`}>
              {doneMap[key] ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />}
              <span className={doneMap[key] ? "text-slate-700" : "text-slate-500"}>{label}</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-5">
        <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.dashboard.recent_orders")}</h3>
        <p className="text-sm text-slate-500">{t("shop.dashboard.no_orders")}</p>
      </Card>
    </div>
  );
}

/* ---------------- Settings ---------------- */
const TEXT_FIELDS = ["shop_name", "tagline_de", "tagline_en", "slug", "logo_url", "cover_url",
  "legal_name", "contact_email", "contact_phone", "address_line1", "address_line2", "postal_code", "city", "country", "vat_id", "custom_domain"];
const LONG_FIELDS = ["impressum", "privacy_policy", "terms", "shipping_policy", "cancellation_policy", "returns_policy"];

function SettingsTab({ settings, refetch }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings), [settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...extra };
      const { data } = await api.put("/shop/admin/settings", payload);
      setForm(data);
      qc.invalidateQueries({ queryKey: ["shop-settings"] });
      qc.invalidateQueries({ queryKey: ["shop-dashboard"] });
      toast.success(t("shop.settings.saved"));
      refetch?.();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };

  const field = (k, labelKey) => (
    <div className="space-y-1.5">
      <Label>{t(labelKey)}</Label>
      <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`shop-field-${k}`} />
    </div>
  );
  const longField = (k, labelKey) => (
    <div className="space-y-1.5">
      <Label>{t(labelKey)}</Label>
      <Textarea rows={3} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`shop-field-${k}`} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="shop-settings">
      <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">{t("shop.status_label")}: <StatusBadge status={form.status} testId="shop-status-badge-settings" /></div>
        <div className="flex gap-2">
          {["draft", "maintenance", "live"].map((s) => (
            <Button key={s} size="sm" variant={form.status === s ? "default" : "outline"} disabled={saving}
              onClick={() => save({ status: s })} data-testid={`shop-set-status-${s}`}>{t(`shop.status.${s}`)}</Button>
          ))}
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.settings.branding")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("shop_name", "shop.settings.shop_name")}
          <div className="space-y-1.5">
            <Label>{t("shop.settings.slug")}</Label>
            <Input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="shop-field-slug" />
            <p className="text-xs text-slate-500">{t("shop.settings.slug_hint")}</p>
          </div>
          {field("tagline_de", "shop.settings.tagline_de")}
          {field("tagline_en", "shop.settings.tagline_en")}
          {field("logo_url", "shop.settings.logo_url")}
          {field("cover_url", "shop.settings.cover_url")}
          <div className="space-y-1.5">
            <Label>{t("shop.settings.primary_color")}</Label>
            <div className="flex gap-2"><input type="color" value={form.primary_color || "#0f172a"} onChange={(e) => set("primary_color", e.target.value)} className="h-9 w-12 rounded border" data-testid="shop-field-primary_color" /><Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("shop.settings.accent_color")}</Label>
            <div className="flex gap-2"><input type="color" value={form.accent_color || "#2563eb"} onChange={(e) => set("accent_color", e.target.value)} className="h-9 w-12 rounded border" data-testid="shop-field-accent_color" /><Input value={form.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("shop.settings.language")}</Label>
            <Select value={form.default_language || "de"} onValueChange={(v) => set("default_language", v)}>
              <SelectTrigger data-testid="shop-field-language"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="de">Deutsch</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.settings.general")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("legal_name", "shop.settings.legal_name")}
          {field("contact_email", "shop.settings.contact_email")}
          {field("contact_phone", "shop.settings.contact_phone")}
          {field("vat_id", "shop.settings.vat_id")}
          {field("address_line1", "shop.settings.address1")}
          {field("address_line2", "shop.settings.address2")}
          {field("postal_code", "shop.settings.postal_code")}
          {field("city", "shop.settings.city")}
          {field("country", "shop.settings.country")}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("shop.settings.custom_domain")}</Label>
            <Input value={form.custom_domain || ""} onChange={(e) => set("custom_domain", e.target.value)} data-testid="shop-field-custom_domain" />
            <p className="text-xs text-slate-500">{t("shop.settings.custom_domain_hint")}</p>
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.settings.legal")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {longField("impressum", "shop.settings.impressum")}
          {longField("privacy_policy", "shop.settings.privacy")}
          {longField("terms", "shop.settings.terms")}
          {longField("shipping_policy", "shop.settings.shipping")}
          {longField("cancellation_policy", "shop.settings.cancellation")}
          {longField("returns_policy", "shop.settings.returns")}
        </div>
      </Card>

      <PaymentSettings />

      <div className="flex justify-end">
        <Button onClick={() => save()} disabled={saving} data-testid="shop-settings-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("shop.settings.save")}</Button>
      </div>
    </div>
  );
}

/* ---------------- Payment methods (Step 5) ---------------- */
function PaymentSettings() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/shop/admin/payment-methods").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/shop/admin/payment-methods", {
        manual_enabled: cfg.manual_enabled, sandbox_enabled: cfg.sandbox_enabled,
        default_method: cfg.default_method, manual_instructions: cfg.manual_instructions,
      });
      setCfg((c) => ({ ...c, ...data }));
      toast.success(t("shop.settings.saved"));
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };
  return (
    <Card className="space-y-4 p-5" data-testid="shop-payment-settings">
      <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("shop.settings.payments")}</h3>
      <div className="space-y-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.manual_enabled} onChange={(e) => set("manual_enabled", e.target.checked)} data-testid="shop-pay-manual-enabled" />
          <span>{t("shop.settings.pay_manual_enabled")}</span>
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.sandbox_enabled} onChange={(e) => set("sandbox_enabled", e.target.checked)} data-testid="shop-pay-sandbox-enabled" />
          <span>{t("shop.settings.pay_sandbox_enabled")}</span>
        </label>
        <div className="space-y-1.5">
          <Label>{t("shop.settings.pay_default")}</Label>
          <Select value={cfg.default_method || "manual_invoice"} onValueChange={(v) => set("default_method", v)}>
            <SelectTrigger data-testid="shop-pay-default" className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual_invoice">{t("shop.storefront.pay_manual_invoice")}</SelectItem>
              <SelectItem value="test_sandbox">{t("shop.storefront.pay_test_sandbox")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("shop.settings.pay_instructions")}</Label>
          <Textarea rows={3} value={cfg.manual_instructions || ""} onChange={(e) => set("manual_instructions", e.target.value)} data-testid="shop-pay-instructions" />
          <p className="text-xs text-slate-500">{t("shop.settings.pay_instructions_hint")}</p>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="shop-pay-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("shop.settings.save")}</Button>
      </div>
    </Card>
  );
}

/* ---------------- Product publication ---------------- */
function ProductImages({ productId }) {
  const { t } = useI18n();
  const [imgs, setImgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const reload = useCallback(async () => {
    try {
      setImgs((await api.get(`/shop/admin/products/${productId}/images`)).data);
    } catch {}
  }, [productId]);
  useEffect(() => { reload(); }, [reload]);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setErr("");
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/shop/admin/products/${productId}/images`, fd); await reload(); }
    catch (ex) { setErr(t(`shop.products.img_err_${ex?.response?.data?.detail || "generic"}`) === `shop.products.img_err_${ex?.response?.data?.detail || "generic"}` ? t("shop.products.img_err_generic") : t(`shop.products.img_err_${ex?.response?.data?.detail}`)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const setPrimary = async (id) => { await api.post(`/shop/admin/products/${productId}/images/${id}/primary`); reload(); };
  const remove = async (id) => { await api.delete(`/shop/admin/products/${productId}/images/${id}`); reload(); };
  return (
    <div className="space-y-2" data-testid="shop-pd-images">
      <Label>{t("shop.products.images")}</Label>
      <div className="flex flex-wrap gap-3">
        {imgs.map((i) => (
          <div key={i.id} className={`relative h-20 w-20 overflow-hidden rounded-lg border ${i.is_primary ? "ring-2 ring-blue-500" : "border-slate-200"}`} data-testid={`shop-img-${i.id}`}>
            <img src={`${BACKEND}${i.url}`} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 px-1 py-0.5">
              {!i.is_primary && <button type="button" onClick={() => setPrimary(i.id)} title={t("shop.products.set_primary")} data-testid={`shop-img-primary-${i.id}`} className="text-[10px] text-white">★</button>}
              {i.is_primary && <span className="text-[10px] text-white">{t("shop.products.primary")}</span>}
              <button type="button" onClick={() => remove(i.id)} aria-label={t("shop.categories.delete")} data-testid={`shop-img-del-${i.id}`} className="text-[10px] text-white">✕</button>
            </div>
          </div>
        ))}
        <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-400 hover:border-slate-400" data-testid="shop-img-upload-label">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-5 w-5" /><span className="text-[10px]">{t("shop.products.add_image")}</span></>}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="shop-img-upload" />
        </label>
      </div>
      {err && <p className="text-xs text-red-500" role="alert">{err}</p>}
    </div>
  );
}

function ProductEditDialog({ product, categories, onClose }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({ ...product }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        visibility: form.visibility, public_name_de: form.public_name_de, public_name_en: form.public_name_en,
        public_description_de: form.public_description_de, public_description_en: form.public_description_en,
        public_price: form.public_price, category_id: form.category_id || null, tags: form.tags,
        stock_display_policy: form.stock_display_policy, backorder_policy: form.backorder_policy,
        min_qty: form.min_qty, max_qty: form.max_qty,
        seo_title_de: form.seo_title_de, seo_title_en: form.seo_title_en,
        seo_description_de: form.seo_description_de, seo_description_en: form.seo_description_en,
      };
      await api.put(`/shop/admin/products/${product.product_id}`, body);
      qc.invalidateQueries({ queryKey: ["shop-products"] });
      qc.invalidateQueries({ queryKey: ["shop-dashboard"] });
      toast.success(t("shop.settings.saved"));
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="shop-product-dialog">
        <DialogHeader><DialogTitle>{product.erp_name_de} — {product.sku}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("shop.products.visibility")}</Label>
              <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
                <SelectTrigger data-testid="shop-pd-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hidden">{t("shop.dashboard.hidden")}</SelectItem>
                  <SelectItem value="draft">{t("shop.status.draft")}</SelectItem>
                  <SelectItem value="published">{t("shop.dashboard.published")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("shop.products.category")}</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => set("category_id", v === "none" ? null : v)}>
                <SelectTrigger data-testid="shop-pd-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("shop.products.no_category")}</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_de}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("shop.products.public_name_de")}</Label><Input value={form.public_name_de || ""} onChange={(e) => set("public_name_de", e.target.value)} placeholder={product.erp_name_de} data-testid="shop-pd-name-de" /></div>
            <div className="space-y-1.5"><Label>{t("shop.products.public_name_en")}</Label><Input value={form.public_name_en || ""} onChange={(e) => set("public_name_en", e.target.value)} placeholder={product.erp_name_en} /></div>
          </div>
          <div className="space-y-1.5"><Label>{t("shop.products.public_desc_de")}</Label><Textarea rows={3} value={form.public_description_de || ""} onChange={(e) => set("public_description_de", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>{t("shop.products.public_desc_en")}</Label><Textarea rows={3} value={form.public_description_en || ""} onChange={(e) => set("public_description_en", e.target.value)} /></div>
          <ProductImages productId={product.product_id} />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("shop.products.public_price")}</Label>
              <Input type="number" step="0.01" value={form.public_price ?? ""} onChange={(e) => set("public_price", e.target.value)} placeholder={product.erp_selling_price} data-testid="shop-pd-price" />
              <p className="text-xs text-slate-500">{t("shop.products.public_price_hint")}</p>
            </div>
            <div className="space-y-1.5"><Label>{t("shop.products.tags")}</Label><Input value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("shop.products.stock_policy")}</Label>
              <Select value={form.stock_display_policy || "in_out"} onValueChange={(v) => set("stock_display_policy", v)}>
                <SelectTrigger data-testid="shop-pd-stock"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">{t("shop.products.stock_exact")}</SelectItem>
                  <SelectItem value="in_out">{t("shop.products.stock_in_out")}</SelectItem>
                  <SelectItem value="hidden">{t("shop.products.stock_hidden")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("shop.products.backorder")}</Label>
              <Select value={form.backorder_policy || "deny"} onValueChange={(v) => set("backorder_policy", v)}>
                <SelectTrigger data-testid="shop-pd-backorder"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deny">{t("shop.products.backorder_deny")}</SelectItem>
                  <SelectItem value="allow">{t("shop.products.backorder_allow")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>{t("shop.products.min_qty")}</Label><Input type="number" value={form.min_qty ?? 1} onChange={(e) => set("min_qty", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t("shop.products.max_qty")}</Label><Input type="number" value={form.max_qty ?? ""} onChange={(e) => set("max_qty", e.target.value)} /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("shop.products.seo_title_de")}</Label><Input value={form.seo_title_de || ""} onChange={(e) => set("seo_title_de", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t("shop.products.seo_title_en")}</Label><Input value={form.seo_title_en || ""} onChange={(e) => set("seo_title_en", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>{t("shop.products.seo_desc_de")}</Label><Textarea rows={2} value={form.seo_description_de || ""} onChange={(e) => set("seo_description_de", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="shop-pd-cancel">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="shop-pd-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("shop.products.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const { data: cats = [] } = useShopCategories();
  const { data, isLoading } = useQuery({ queryKey: ["shop-products", q], queryFn: async () => (await api.get(`/shop/admin/products?q=${encodeURIComponent(q)}&page_size=100`)).data });
  const items = data?.items || [];
  const visBadge = (v) => v === "published" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">{t("shop.dashboard.published")}</Badge>
    : v === "hidden" ? <Badge variant="secondary">{t("shop.dashboard.hidden")}</Badge> : <Badge variant="outline">{t("shop.status.draft")}</Badge>;
  return (
    <div className="space-y-4" data-testid="shop-products">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shop.products.search")} className="max-w-sm" data-testid="shop-products-search" />
      <Card className="overflow-x-auto">
        {isLoading ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : items.length === 0 ? <p className="p-6 text-sm text-slate-500">{t("shop.products.none")}</p>
            : (
              <Table data-testid="shop-products-table">
                <TableHeader><TableRow>
                  <TableHead>{t("shop.products.erp_name")}</TableHead><TableHead>SKU</TableHead>
                  <TableHead>{t("shop.products.effective_price")}</TableHead><TableHead>{t("shop.products.visibility")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.map((p) => (
                    <TableRow key={p.product_id} data-testid={`shop-product-row-${p.product_id}`}>
                      <TableCell className="font-medium">{p.public_name_de || p.erp_name_de}</TableCell>
                      <TableCell className="text-slate-500">{p.sku}</TableCell>
                      <TableCell className="tabular-nums">€ {p.effective_price_net} <span className="text-xs text-slate-400">+{p.vat_rate}%</span></TableCell>
                      <TableCell>{visBadge(p.visibility)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(p)} data-testid={`shop-edit-${p.product_id}`}><Pencil className="h-4 w-4" />{t("shop.products.edit")}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </Card>
      {editing && <ProductEditDialog product={editing} categories={cats} onClose={() => setEditing(null)} />}
    </div>
  );
}

/* ---------------- Categories ---------------- */
function CategoriesTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: cats = [], isLoading } = useShopCategories();
  const [nameDe, setNameDe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!nameDe.trim()) return;
    setBusy(true);
    try { await api.post("/shop/admin/categories", { name_de: nameDe.trim(), name_en: nameEn.trim() || null }); setNameDe(""); setNameEn(""); qc.invalidateQueries({ queryKey: ["shop-categories"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/shop/admin/categories/${id}`); qc.invalidateQueries({ queryKey: ["shop-categories"] }); } catch (e) { toast.error(apiErrorMessage(e, t)); } };
  return (
    <div className="space-y-4" data-testid="shop-categories">
      <Card className="flex flex-col gap-2 p-4 sm:flex-row">
        <Input value={nameDe} onChange={(e) => setNameDe(e.target.value)} placeholder={t("shop.categories.name_de")} data-testid="shop-cat-name-de" />
        <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder={t("shop.categories.name_en")} data-testid="shop-cat-name-en" />
        <Button onClick={add} disabled={busy || !nameDe.trim()} data-testid="shop-cat-add"><Plus className="h-4 w-4" />{t("shop.categories.add")}</Button>
      </Card>
      <Card>
        {isLoading ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : cats.length === 0 ? <p className="p-6 text-sm text-slate-500">{t("shop.categories.none")}</p>
            : <ul className="divide-y dark:divide-slate-700">
                {cats.map((c) => (
                  <li key={c.id} className="flex items-center justify-between p-3" data-testid={`shop-cat-${c.id}`}>
                    <span className="text-sm">{c.name_de}{c.name_en ? ` / ${c.name_en}` : ""}</span>
                    <Button variant="ghost" size="sm" onClick={() => del(c.id)} data-testid={`shop-cat-del-${c.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </li>
                ))}
              </ul>}
      </Card>
    </div>
  );
}

/* ---------------- Orders ---------------- */
function OrdersTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["shop-orders"], queryFn: async () => (await api.get("/shop/admin/orders")).data });
  const act = async (id, action) => {
    try { await api.post(`/shop/admin/orders/${id}/payment`, { action }); qc.invalidateQueries({ queryKey: ["shop-orders"] }); toast.success(t("shop.settings.saved")); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const fulfil = async (id, status, askTracking = false) => {
    const body = { status };
    if (askTracking) {
      const carrier = window.prompt(t("shop.orders.carrier")) || "";
      const tn = window.prompt(t("shop.orders.tracking")) || "";
      body.carrier = carrier; body.tracking_number = tn;
    }
    try { await api.post(`/shop/admin/orders/${id}/fulfillment`, body); qc.invalidateQueries({ queryKey: ["shop-orders"] }); toast.success(t("shop.settings.saved")); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const payBadge = (s) => s === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">paid</Badge>
    : s === "cancelled" || s === "failed" ? <Badge variant="destructive">{s}</Badge> : <Badge variant="secondary">{s}</Badge>;
  return (
    <Card className="overflow-x-auto" data-testid="shop-orders">
      {isLoading ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        : orders.length === 0 ? <p className="p-6 text-sm text-slate-500">{t("shop.orders.none")}</p>
          : (
            <Table data-testid="shop-orders-table">
              <TableHeader><TableRow>
                <TableHead>{t("shop.orders.order_no")}</TableHead><TableHead>{t("shop.orders.customer")}</TableHead>
                <TableHead>{t("shop.orders.total")}</TableHead><TableHead>{t("shop.orders.payment")}</TableHead>
                <TableHead>{t("shop.orders.shipping")}</TableHead><TableHead>{t("shop.orders.fulfillment")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.order_id} data-testid={`shop-order-row-${o.order_id}`}>
                    <TableCell className="font-medium">{o.order_number}</TableCell>
                    <TableCell className="text-slate-500">{o.contact_email}</TableCell>
                    <TableCell className="tabular-nums">€ {o.total}</TableCell>
                    <TableCell>{payBadge(o.payment_status)}</TableCell>
                    <TableCell className="text-slate-500">{t(`shop.orders.f_${o.fulfillment_status || "new"}`)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {o.payment_status === "pending" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => act(o.order_id, "mark_paid")} data-testid={`shop-order-paid-${o.order_id}`}>{t("shop.orders.mark_paid")}</Button>
                            <Button variant="ghost" size="sm" onClick={() => act(o.order_id, "cancel")} data-testid={`shop-order-cancel-${o.order_id}`}><Ban className="h-4 w-4 text-red-500" /></Button>
                          </>
                        )}
                        {(o.fulfillment_status || "new") === "new" && o.payment_status !== "cancelled" && <Button variant="ghost" size="sm" onClick={() => fulfil(o.order_id, "processing")} data-testid={`shop-order-processing-${o.order_id}`}>{t("shop.orders.set_processing")}</Button>}
                        {o.fulfillment_status === "processing" && <Button variant="ghost" size="sm" onClick={() => fulfil(o.order_id, "ready")} data-testid={`shop-order-ready-${o.order_id}`}>{t("shop.orders.set_ready")}</Button>}
                        {o.fulfillment_status === "ready" && <Button variant="ghost" size="sm" onClick={() => fulfil(o.order_id, "shipped", true)} data-testid={`shop-order-shipped-${o.order_id}`}>{t("shop.orders.set_shipped")}</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
    </Card>
  );
}

/* ---------------- B2B customer access ---------------- */
function B2BTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["shop-b2b"], queryFn: async () => (await api.get("/shop/admin/b2b-customers")).data });
  const [busyId, setBusyId] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: ["shop-b2b"] });
  const invite = async (r) => {
    if (!r.email) { toast.error(t("shop.b2b.no_email")); return; }
    setBusyId(r.customer_id);
    try {
      const { data } = await api.post(`/shop/admin/b2b-customers/${r.customer_id}/invite`, {});
      if (data.invite_link) { try { await navigator.clipboard.writeText(data.invite_link); } catch {} toast.success(t("shop.b2b.link_copied")); }
      invalidate();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusyId(""); }
  };
  const disable = async (r) => { setBusyId(r.customer_id); try { await api.post(`/shop/admin/b2b-customers/${r.customer_id}/disable`); invalidate(); } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusyId(""); } };
  const enable = async (r) => { setBusyId(r.customer_id); try { await api.post(`/shop/admin/b2b-customers/${r.customer_id}/enable`); invalidate(); } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusyId(""); } };
  const stBadge = (s) => s === "enabled" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">{t("shop.b2b.st_enabled")}</Badge>
    : s === "invited" ? <Badge variant="secondary">{t("shop.b2b.st_invited")}</Badge>
      : s === "disabled" ? <Badge variant="destructive">{t("shop.b2b.st_disabled")}</Badge> : <Badge variant="outline">{t("shop.b2b.st_none")}</Badge>;
  return (
    <div className="space-y-3" data-testid="shop-b2b">
      <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900 dark:bg-blue-950/40">{t("shop.b2b.help")}</p>
      <Card className="overflow-x-auto">
        {isLoading ? <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="p-6 text-sm text-slate-500">{t("shop.b2b.none")}</p>
            : (
              <Table data-testid="shop-b2b-table">
                <TableHeader><TableRow>
                  <TableHead>{t("shop.b2b.customer")}</TableHead><TableHead>{t("shop.b2b.email")}</TableHead>
                  <TableHead>{t("shop.b2b.status")}</TableHead><TableHead>{t("shop.b2b.last_activity")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.customer_id} data-testid={`shop-b2b-row-${r.customer_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-slate-500">{r.email || "—"}</TableCell>
                      <TableCell>{stBadge(r.access_status)}</TableCell>
                      <TableCell className="text-slate-500">{r.last_activity ? new Date(r.last_activity).toLocaleString() : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" disabled={busyId === r.customer_id} onClick={() => invite(r)} data-testid={`shop-b2b-invite-${r.customer_id}`}>{r.has_password ? t("shop.b2b.reinvite") : t("shop.b2b.invite")}</Button>
                          {r.access_status === "enabled"
                            ? <Button variant="ghost" size="sm" disabled={busyId === r.customer_id} onClick={() => disable(r)} data-testid={`shop-b2b-disable-${r.customer_id}`}><Ban className="h-4 w-4 text-red-500" /></Button>
                            : r.has_password ? <Button variant="ghost" size="sm" disabled={busyId === r.customer_id} onClick={() => enable(r)} data-testid={`shop-b2b-enable-${r.customer_id}`}>{t("shop.b2b.enable")}</Button> : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </Card>
    </div>
  );
}

/* ---------------- Shell ---------------- */
export default function ShopAdmin() {
  const { t } = useI18n();
  const { data: settings, isLoading, refetch } = useShopSettings();
  if (isLoading || !settings) return <div className="p-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;
  const storefrontUrl = settings.slug ? `${BACKEND?.replace(/\/$/, "")}/shop/${settings.slug}` : null;
  return (
    <div data-testid="shop-admin">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t("shop.title")} subtitle={t("shop.subtitle")} />
        <div className="flex items-center gap-3">
          <StatusBadge status={settings.status} />
          {storefrontUrl && settings.status !== "draft" && (
            <a href={storefrontUrl} target="_blank" rel="noreferrer" data-testid="shop-view-storefront"
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
              <ExternalLink className="h-4 w-4" />{t("shop.view_storefront")}
            </a>
          )}
        </div>
      </div>
      <Tabs defaultValue="dashboard">
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="shop-tab-dashboard"><Store className="mr-1.5 h-4 w-4" />{t("shop.tabs.dashboard")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="shop-tab-settings">{t("shop.tabs.settings")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="shop-tab-products"><Package className="mr-1.5 h-4 w-4" />{t("shop.tabs.products")}</TabsTrigger>
          <TabsTrigger value="categories" data-testid="shop-tab-categories"><Layers className="mr-1.5 h-4 w-4" />{t("shop.tabs.categories")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="shop-tab-orders"><Package className="mr-1.5 h-4 w-4" />{t("shop.tabs.orders")}</TabsTrigger>
          <TabsTrigger value="b2b" data-testid="shop-tab-b2b"><User className="mr-1.5 h-4 w-4" />{t("shop.b2b.tab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab settings={settings} /></TabsContent>
        <TabsContent value="settings"><SettingsTab settings={settings} refetch={refetch} /></TabsContent>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="orders"><OrdersTab /></TabsContent>
        <TabsContent value="b2b"><B2BTab /></TabsContent>
      </Tabs>
    </div>
  );
}
